import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bot, AlertTriangle, Trash2, Loader2, X, Send, Baby } from 'lucide-react';
import { useAIChat } from '../hooks/useAIChat';
import { ChatMessage, TypingIndicator, ChatLimitPaywall } from '../components';
import { TopBar, BottomNav } from '@/components/layout';
import { Disclaimer } from '@/components/Disclaimer';
import { isNative } from '@/lib/capacitor';
import { usePregnancyStore } from '@/store/pregnancyStore';
import { useTranslation } from '@parentpath/i18n';

export default function AIChatPage() {
  const { t } = useTranslation('aiChat');
  const navigate = useNavigate();
  const {
    messages,
    usage,
    settings,
    isLoading,
    isSending,
    error,
    sendMessage,
    clearConversation,
    isLimitReached,
    isUnlimited,
  } = useAIChat();

  const { appMode, lastCompletedPregnancy } = usePregnancyStore();
  const isBabyMode = appMode === 'baby' && lastCompletedPregnancy?.completion_type === 'birth';

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [showPaywallModal, setShowPaywallModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [inputValue, setInputValue] = useState('');

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Handle message send - show paywall if limit reached
  const handleSendMessage = async (content: string) => {
    if (!content.trim()) return;
    if (isLimitReached) {
      setShowPaywallModal(true);
      return;
    }
    setInputValue('');
    await sendMessage(content);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(inputValue);
    }
  };

  const handleDelete = () => {
    clearConversation();
    setShowDeleteConfirm(false);
  };

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = 'auto';
    const maxHeight = 96; // 3 lines: 3 * 24px (leading-6) + 24px (py-3 padding)
    textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, [inputValue]);

  // Questions count display - show remaining questions
  const questionsCount = usage?.questions_count || 0;
  const questionsLimit = settings.ai_questions_limit;
  const questionsDisplay = isUnlimited
    ? t('header.unlimited')
    : t('header.questionsCount', { count: questionsCount, limit: questionsLimit });

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  // Feature disabled
  if (!settings.ai_chat_enabled) {
    return (
      <div className="min-h-screen">
        <TopBar />
        <BottomNav />
        <div className="flex items-center justify-center min-h-screen p-6">
          <div className="text-center text-white">
            <Bot className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <h2 className="text-lg font-semibold mb-2">
              {t('featureDisabled.title')}
            </h2>
            <p className="text-white/70">
              {t('featureDisabled.description')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Calculate positions based on native/web
  const mainMarginTop = isNative
    ? 'calc(env(safe-area-inset-top, 0px) + 180px)'
    : '160px';

  const mainMarginBottom = isNative
    ? 'calc(env(safe-area-inset-bottom,0px) + 120px)'
    : '130px';

  const inputBottom = isNative
    ? 'calc(env(safe-area-inset-bottom,0px) + 60px)'
    : '85px';

  const isSendDisabled = isSending || !inputValue.trim();

  const suggestions = isBabyMode
    ? (t('emptyState.suggestionsBaby', { returnObjects: true }) as string[])
    : (t('emptyState.suggestionsPregnancy', { returnObjects: true }) as string[]);

  return (
    <div className="min-h-[100dvh] flex flex-col bg-white">
      {/* TopBar - renders as container on native, floating on web */}
      <TopBar />

      {/* Chat Header - below TopBar */}
      <header
        className="fixed left-0 right-0 z-30 px-4 pb-3 bg-pp-dark"
        style={{
          top: 0,
          paddingTop: isNative
            ? 'calc(env(safe-area-inset-top, 0px) + 56px)'
            : '60px',
        }}
      >
        <div className="max-w-[767px] mx-auto">
          {/* Title */}
          <h1 className="text-white text-2xl font-semibold mb-2">{t('pageTitle')}</h1>

          {/* Subtitle Row */}
          <div className="flex items-center justify-between mt-2">
            {/* Warning */}
            <div className="flex items-center gap-1.5 px-2 py-1 bg-white/10 rounded-[5px]">
              <AlertTriangle className="w-4 h-4 text-pp-orange" />
              <span className="text-pp-orange text-xs font-medium">{t('header.disclaimer')}</span>
            </div>

            {/* Questions Badge & Delete */}
            <div className="flex items-center gap-2">
              <span className="bg-white/10 border border-white/25 rounded-[5px] px-2.5 py-1 text-xs font-medium text-white">
                {questionsDisplay}
              </span>
              {messages.length > 0 && (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="bg-pp-orange rounded-[5px] p-1.5"
                  title={t('header.deleteChat')}
                >
                  <Trash2 className="w-4 h-4 text-white" />
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Scrollable Chat Area */}
      <main
        className="flex-1 overflow-y-auto"
        style={{
          marginTop: mainMarginTop,
          marginBottom: mainMarginBottom,
        }}
      >
        <div className="max-w-[767px] mx-auto px-4 pt-3 pb-6 min-h-full">
          {/* Empty State */}
          {messages.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-center px-4 py-12">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
                isBabyMode ? 'bg-pink-100' : 'bg-pp-teal/10'
              }`}>
                {isBabyMode ? (
                  <Baby className="w-8 h-8 text-pink-500" />
                ) : (
                  <Bot className="w-8 h-8 text-pp-teal" />
                )}
              </div>
              <h2 className="text-lg font-semibold text-pp-dark mb-2">
                {t('emptyState.greeting')}
              </h2>
              <p className="text-gray-500 text-sm mb-6 max-w-xs">
                {isBabyMode
                  ? t('emptyState.descriptionBaby')
                  : t('emptyState.descriptionPregnancy')}
              </p>

              {/* Suggestion Chips */}
              <div className="flex flex-wrap justify-center gap-2">
                {suggestions.map((suggestion) => (
                  <button
                    key={suggestion}
                    onClick={() => handleSendMessage(suggestion)}
                    className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-full text-sm text-gray-700 transition-colors"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Messages */}
          {messages.map((message) => (
            <ChatMessage key={message.id} message={message} />
          ))}

          {/* Typing Indicator */}
          {isSending && <TypingIndicator />}

          {/* Error */}
          {error && (
            <div className="bg-red-50 text-red-600 text-sm px-4 py-2 rounded-lg mb-4">
              {error}
            </div>
          )}

          {/* Scroll anchor */}
          <div ref={messagesEndRef} />

          {/* Disclaimer - /chat läuft nicht über AppLayout und braucht die
              Einbindung deshalb manuell. Bewusst NACH dem Scroll-Anker: das
              Auto-Scroll springt weiterhin auf die letzte Nachricht, der
              Disclaimer liegt darunter am Ende des Scroll-Containers. */}
          <Disclaimer />
        </div>
      </main>

      {/* Fixed Chat Input - above BottomNav */}
      <div
        className="fixed left-0 right-0 z-40 px-4 py-2"
        style={{ bottom: inputBottom }}
      >
        <div className="max-w-[767px] mx-auto">
          <div className="relative rounded-pp">
            <textarea
              ref={textareaRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('input.placeholder')}
              rows={1}
              className="w-full bg-white border border-pp-orange rounded-pp py-3 pl-4 pr-14 outline-none text-base placeholder:text-pp-dark/50 resize-none max-h-[96px] leading-6 shadow-lg"
              disabled={isSending}
            />
            <button
              onClick={() => handleSendMessage(inputValue)}
              disabled={isSendDisabled}
              className="absolute right-0 top-0 h-full w-12 rounded-r-pp flex items-center justify-center"
            >
              <Send className={`w-5 h-5 ${isSendDisabled ? 'text-pp-orange/50' : 'text-pp-orange'}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <BottomNav />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <>
          <div className="bottom-sheet-backdrop" onClick={() => setShowDeleteConfirm(false)} />
          <div className="bottom-sheet-container">
            <div className="bottom-sheet-modal">
              <div className="bottom-sheet-content">
                <h3 className="text-lg font-semibold text-white mb-2">{t('deleteModal.title')}</h3>
                <p className="text-white/70">
                  {t('deleteModal.description')}
                </p>
              </div>
              <div className="bottom-sheet-footer">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30"
                >
                  {t('deleteModal.cancel')}
                </button>
                <button
                  onClick={handleDelete}
                  className="flex-1 py-2.5 rounded-xl bg-red-500 text-white font-medium"
                >
                  {t('deleteModal.delete')}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Paywall Modal */}
      {showPaywallModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
          <div className="relative bg-white rounded-2xl max-w-sm w-full max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setShowPaywallModal(false)}
              className="absolute top-3 right-3 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <ChatLimitPaywall
              questionsUsed={usage?.questions_count || 0}
              questionsLimit={settings.ai_questions_limit}
              onUpgrade={() => {
                setShowPaywallModal(false);
                navigate('/subscription');
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
